import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { assistantNavigationHref } from '@/lib/assistantNavigation';
import { parseAssistantGuide, type AssistantGuideCommand } from '@/lib/assistantGuide';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { assistantPrincipalSnapshot, sameAssistantPrincipal } from '@/lib/assistantPrincipal';
import { api, ApiError } from '@/lib/api';
import { GUIDES } from '@/lib/guides/definitions';
import { getGuidePageContext, useGuideStore } from '@/lib/guides/engine';
import {
  useAssistantStore,
  type AssistantActionOutcome,
  type AssistantFailureReason,
  type AssistantMessage,
  type ConfirmationPresenter,
  type ConfirmationRetry,
  type PageContext,
} from '@/stores/assistantStore';
import {
  assistantLanguageOf,
  cancelledOutcomeOf,
  confirmedOutcomeOf,
  failureOf,
  invalidateAssistantWriteCaches,
  mayHaveWrittenTurn,
  pinnedReceivedAtOf,
  presenterOf,
  settledPresenterOf,
  settledTurnOf,
} from './assistantOutcome';
import { boundQuoteResult, type QuoteResultAction } from '@/lib/quoteResultAction';
import { queryKeys } from './queryKeys';

/**
 * Delivery model: WebSocket-primary.
 *
 * The staff-api emits `assistant:response` and `assistant:pending_confirmation`
 * to the per-user assistant room. `useAssistantRealtime` renders messages from
 * those events. The HTTP `/chat`, `/confirm`, `/cancel` responses are kept as a
 * fallback (in case the socket is briefly disconnected) and are deduplicated
 * client-side via the persisted `messageId`. See AssistantPanel for the WS
 * subscription mount point.
 */
interface ChatResponse {
  /** Transient command, returned to the requesting tab only. */
  navigation?: unknown;
  /**
   * Transient guide command, returned on the HTTP reply of the requesting tab
   * only (#3898). Socket events do not carry it.
   */
  guide?: unknown;
  conversationId: string;
  messageId?: string;
  type: 'message' | 'pending_confirmation' | 'action_confirmed' | 'action_cancelled';
  content?: string;
  actionId?: string;
  toolName?: string;
  description?: string;
  params?: Record<string, unknown>;
  /**
   * `pending_confirmation` from the harness path (#2180), and (#2587) the
   * settled `action_confirmed` / `action_cancelled` it ended in.
   */
  presenter?: ConfirmationPresenter;
  /** A preview refusal's or a failed attempt's reason code (#2587). */
  failure?: AssistantFailureReason;
  /** Additive (#2821): the receipt time a settled quote acceptance sent. */
  pinnedReceivedAt?: string;
  /** Additive (#2821): the reply replays an action completed earlier. */
  alreadyCompleted?: boolean;
  quoteResult?: QuoteResultAction;
  /**
   * Additive, `action_confirmed` only, and sent by the harness path alone
   * (`deliverHarnessResult` in `services/staff-api/src/routes/assistant.ts`).
   *
   * `'unknown'` means the write may or may not have been applied and can never
   * be re-run. The portal never receives `409 IDEMPOTENCY_OUTCOME_UNKNOWN`
   * itself: the orchestrator consumes that 409, spends the plan rather than
   * re-POSTing it, and reports the outcome here instead (#2104).
   *
   * Also `'unknown'` on an `action_cancelled` tapped after a confirmed attempt
   * that may still commit (#2597).
   */
  outcome?: AssistantActionOutcome;
}

interface MessagesResponse {
  messages: Array<{
    id: string;
    direction: string;
    content: string | null;
    createdAt: string;
    metadata?: Record<string, unknown>;
  }>;
  hasMore: boolean;
}

export interface ConversationSummary {
  id: string;
  status: string;
  lastMessageAt: string | null;
  createdAt: string;
}

interface ConversationsResponse {
  conversations?: ConversationSummary[];
}

/**
 * What a failed `/confirm` or `/cancel` tells us about the write.
 *
 * The first three are refused *before* the tool runs, so they are safe to
 * report as "nothing was executed". Everything else is `ambiguous`: the legacy
 * `/confirm` executes the tool first and only then calls the AI gateway and
 * inserts the message, so a 500 — or a dropped connection — can land on a
 * write that already committed.
 */
type ConfirmationFailure = 'expired' | 'forbidden' | 'engineMismatch' | 'ambiguous';

/** The `error` code in an API error body, when there is one. */
function errorCodeOf(error: ApiError): string | undefined {
  const { data } = error;
  if (typeof data !== 'object' || data === null) return undefined;
  const code = (data as { error?: unknown }).error;
  return typeof code === 'string' ? code : undefined;
}

/**
 * What the user was confirming, read from the LIVE store.
 *
 * `confirmAction` and `cancelAction` clear the card before the POST, so by the
 * time an error lands the pending action is gone and the copy has nothing left
 * to name. Reading `getState()` here rather than closing over a rendered value
 * is deliberate: a closure would keep working if the capture were moved below
 * the clear, hiding the ordering this depends on.
 */
function describeAction(actionId: string): string | undefined {
  const pending = useAssistantStore.getState().pendingConfirmation;
  return pending?.actionId === actionId ? pending.description : undefined;
}

/**
 * Whether the ACTION — not this request — may have written something.
 *
 * `expired` is the subtle one, and it belongs here for BOTH verbs. A 410 means
 * the plan is gone, and it is gone either because it timed out (nothing ran) or
 * because something else claimed it first and ran the tool.
 * `claimPendingAction` is one GETDEL shared by `/confirm` and `/cancel`, so a
 * double tap, a second tab, or a cancel that loses the race to a confirm all
 * land here looking at a write this request cannot see.
 *
 * `ambiguous` counts for confirm only: the legacy `/confirm` executes the tool
 * before it answers, so a 500 can sit on a committed write. `/cancel` performs
 * no domain write at all, so its 500 cannot have created one.
 */
function mayHaveWritten(
  scope: 'confirmFailed' | 'cancelFailed',
  failure: ConfirmationFailure
): boolean {
  if (failure === 'expired') return true;
  return scope === 'confirmFailed' && failure === 'ambiguous';
}

/**
 * The transcript message for a settled reply, typed by the REPLY (#2587
 * review P1): a `/cancel` that replays a confirmation another tab spent is an
 * `action_confirmed`, and rendering it as a cancel both mislabels it and — on
 * HTTP-only delivery — skips the refresh a write needs.
 */
function settledMessage(
  messageId: string,
  response: ChatResponse,
  fallback: Parameters<typeof settledTurnOf>[1]
): { message: AssistantMessage; mayHaveWritten: boolean } {
  const turn = settledTurnOf(response, fallback);
  return {
    message: {
      id: messageId,
      conversationId: response.conversationId,
      role: 'assistant',
      content: response.content ?? '',
      createdAt: new Date().toISOString(),
      type: turn.type,
      actionId: turn.actionId,
      // Each only when present, so the merge with the socket event for the
      // same messageId cannot clobber a field in either arrival order.
      ...(turn.outcome ? { outcome: turn.outcome } : {}),
      ...(turn.presenter ? { presenter: turn.presenter } : {}),
      ...(turn.failure ? { failure: turn.failure } : {}),
      ...(turn.pinnedReceivedAt ? { pinnedReceivedAt: turn.pinnedReceivedAt } : {}),
      ...(turn.alreadyCompleted ? { alreadyCompleted: true } : {}),
      ...(turn.quoteResult ? { quoteResult: turn.quoteResult } : {}),
    },
    mayHaveWritten: turn.unrecognised || mayHaveWrittenTurn(turn.type, turn.outcome),
  };
}

function classifyFailure(error: unknown): ConfirmationFailure {
  // A dropped connection rejects with a bare `TypeError`, not an `ApiError`.
  if (!(error instanceof ApiError)) return 'ambiguous';
  if (error.status === 410) return 'expired';
  if (error.status === 403) return 'forbidden';
  /**
   * Keyed on the body, never on the status. The harness answers a rejected
   * plan with `409 { error: 'Conflict', reason }` — ordinary staleness, which
   * must fall through to the ambiguous path. A `409 CONFIRMATION_ENGINE_MISMATCH`
   * cannot be returned: the legacy in-process engine and `rejectForeignEngineId`
   * were deleted in #2349 (c); the harness is the only portal engine.
   */
  if (error.status === 409 && errorCodeOf(error) === 'CONFIRMATION_ENGINE_MISMATCH') {
    return 'engineMismatch';
  }
  return 'ambiguous';
}

/**
 * Page context for one chat turn. The live guide step is merged in here,
 * and a step left on the stored page is not sent once the guide has ended.
 */
function chatRequestContext(current: PageContext | null): PageContext | undefined {
  const guide = getGuidePageContext();
  if (!current && !guide) return undefined;
  const next: PageContext = current ? { ...current } : { route: '' };
  if (guide) next.guide = guide;
  else delete next.guide;
  return next;
}

/** Run a validated guide command. The guide definitions stay on the client. */
function applyAssistantGuide(command: AssistantGuideCommand): void {
  const guides = useGuideStore.getState();
  if (command.kind === 'start') {
    guides.start(GUIDES[command.guideId]);
    return;
  }
  const controls = {
    back: guides.back,
    skip: guides.skip,
    cancel: guides.cancel,
  };
  controls[command.action]();
}

const RECEIPT_REFUSALS = new Set([
  'quote_receipt_time_required',
  'quote_receipt_time_invalid',
  'quote_receipt_time_future',
]);

/**
 * A confirm refusal after which the SAME plan is still confirmable (#2821):
 * the harness kept it and said why. A receipt-time refusal arrives as
 * `409 { code }`; "nothing ran, send it again" as a 503 naming this action.
 * Anything else keeps today's handling (the card goes, the failure is said).
 */
function keptPlanRefusal(
  error: unknown,
  actionId: string
): { reason: ConfirmationRetry['reason']; pinnedReceivedAt?: string } | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = typeof error.data === 'object' && error.data !== null ? (error.data as Record<string, unknown>) : {};
  // EDITABLE: refused before anything could be recorded.
  if (error.status === 409 && typeof data.code === 'string' && RECEIPT_REFUSALS.has(data.code)) {
    return { reason: data.code as ConfirmationRetry['reason'] };
  }
  // RECONCILING: an attempt may exist; the server names the time it holds.
  if (error.status === 503 && data.actionId === actionId) {
    return {
      reason: 'retryable',
      ...(typeof data.pinnedReceivedAt === 'string' ? { pinnedReceivedAt: data.pinnedReceivedAt } : {}),
    };
  }
  return undefined;
}

export function useAssistantChat() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('assistant');
  const queryClient = useQueryClient();
  const {
    conversationId,
    currentContext,
    setConversationId,
    addMessage,
    upsertAssistantMessage,
    setLoading,
    setPendingConfirmation,
    setConfirmationRetry,
    markSettled,
    armPendingConfirmation,
    mergeSettled,
    beginHistoryLoad,
    applyHistoryMessages,
    clearConversation,
  } = useAssistantStore();

  /**
   * Every query family an assistant write can touch
   * (`ASSISTANT_WRITE_QUERY_FAMILIES` in `assistantOutcome.ts`). Called only where a
   * write MAY have landed — never on a plain cancel, which creates nothing,
   * and never on a failure the server refused before executing. A cancel the
   * server marks `outcome: 'unknown'` is the exception (#2597): the confirmed
   * attempt before it may still have committed.
   */
  const invalidateAfterPossibleWrite = useCallback(() => {
    invalidateAssistantWriteCaches(queryClient);
  }, [queryClient]);

  /** Validated fi/en/sv: UI language, else the user's preferredLanguage, else fi. */
  const resolveLanguage = useCallback(() => {
    const me = queryClient.getQueryData<{ preferredLanguage?: string | null }>(queryKeys.auth.me());
    return assistantLanguageOf(i18n.language, me?.preferredLanguage);
  }, [queryClient, i18n.language]);

  /**
   * Put the failure in the transcript, where it stays. A toast is the wrong
   * home for "your write may have happened".
   */
  const reportFailure = useCallback(
    (
      scope: 'confirmFailed' | 'cancelFailed',
      failure: ConfirmationFailure,
      action: string | undefined
    ) => {
      const lines = [t(`confirmation.${scope}.${failure}`)];
      if (action) lines.push(t('confirmation.failedAction', { action }));
      addMessage({
        id: crypto.randomUUID(),
        role: 'assistant',
        content: lines.join('\n\n'),
        createdAt: new Date().toISOString(),
        type: 'message',
        // Application-authored, and `action` is the card's description — data
        // that can hold a hostile customer name. Plain text, never Markdown.
        plain: true,
      });
    },
    [t, addMessage]
  );

  const sendMessage = useCallback(
    async (message: string) => {
      const turnPrincipal = assistantPrincipalSnapshot();
      // Add user message to UI immediately
      const userMessage: AssistantMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: message,
        createdAt: new Date().toISOString(),
        type: 'message',
      };
      addMessage(userMessage);
      setLoading(true);
      // A new turn ends an EDITABLE correction. A RECONCILING (locked) card
      // stays locked on its server pin: the turn may fail or answer in plain
      // text and leave the card up, and it must never become editable while
      // the server holds a pinned time for it.
      if (!useAssistantStore.getState().confirmationRetry?.locked) setConfirmationRetry(null);

      try {
        const response = await api.post<ChatResponse>('/assistant/chat', {
          message,
          conversationId: conversationId ?? undefined,
          context: chatRequestContext(currentContext),
          language: resolveLanguage(),
          quoteReviewVersion: 1,
          workOrderReviewVersion: 1,
          quoteReceiptConfirmation: 1,
          quoteItemChangeReview: 1,
          quoteServiceLineReview: 1,
          quoteServiceAddReview: 1,
        });

        // Update conversation ID if new
        if (!conversationId && response.conversationId) {
          setConversationId(response.conversationId);
        }

        // WS event is the primary delivery path; HTTP body is a dedupe-safe
        // fallback so the message still renders if the socket is offline.
        if (response.type === 'pending_confirmation') {
          if (response.messageId) {
            upsertAssistantMessage({
              id: response.messageId,
              role: 'assistant',
              content: response.description ?? '',
              createdAt: new Date().toISOString(),
              type: 'pending_confirmation',
              actionId: response.actionId,
              toolName: response.toolName,
              params: response.params,
              ...(response.presenter ? { presenter: response.presenter } : {}),
            });
          }
          // Through the one guard: a late reply cannot re-arm a settled action.
          armPendingConfirmation({
            actionId: response.actionId!,
            toolName: response.toolName!,
            description: response.description!,
            params: response.params ?? {},
            ...(response.presenter ? { presenter: response.presenter } : {}),
          });
        } else if (response.messageId) {
          const failure = failureOf(response.failure);
          upsertAssistantMessage({
            id: response.messageId,
            role: 'assistant',
            content: response.content ?? '',
            createdAt: new Date().toISOString(),
            type: 'message',
            // A preview refusal (#2587): rendered from the code, not `content`.
            ...(failure ? { failure } : {}),
          });
        }
        const sameTurn =
          sameAssistantPrincipal(turnPrincipal) &&
          useAssistantStore.getState().conversationId === response.conversationId;
        const href = response.type === 'message' ? assistantNavigationHref(response.navigation) : undefined;
        if (href && sameTurn) {
          void navigate(href);
        }
        // `guide` is only on this HTTP reply, like `navigation`: a socket
        // event for the same message must not start the guide in another tab.
        const guideCommand = response.type === 'message' ? parseAssistantGuide(response.guide) : undefined;
        if (guideCommand && sameTurn) {
          applyAssistantGuide(guideCommand);
        }
      } catch {
        // `/chat` writes nothing, so every failure here reads the same: the
        // turn did not happen. No status mapping to do.
        const errMsg: AssistantMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: t('errorMessage'),
          createdAt: new Date().toISOString(),
          type: 'message',
          plain: true,
        };
        addMessage(errMsg);
      } finally {
        setLoading(false);
      }
    },
    [
      conversationId,
      currentContext,
      addMessage,
      upsertAssistantMessage,
      setConversationId,
      setLoading,
      setConfirmationRetry,
      armPendingConfirmation,
      t,
      resolveLanguage,
      navigate,
    ]
  );

  const confirmAction = useCallback(
    async (actionId: string, fields?: { readonly approvalReceivedAt?: string }) => {
      if (!conversationId) return;
      const action = describeAction(actionId);
      // Kept to put the card back if the server refuses but keeps the plan.
      const card = useAssistantStore.getState().pendingConfirmation;
      setLoading(true);
      setPendingConfirmation(null);
      setConfirmationRetry(null);

      try {
        const response = await api.post<ChatResponse>('/assistant/confirm', {
          conversationId,
          actionId,
          language: resolveLanguage(),
          quoteReviewVersion: 1,
          workOrderReviewVersion: 1,
          quoteReceiptConfirmation: 1,
          quoteItemChangeReview: 1,
          quoteServiceLineReview: 1,
          quoteServiceAddReview: 1,
          // The receipt time the staff member confirmed on the card (#2821).
          ...(fields?.approvalReceivedAt ? { approvalReceivedAt: fields.approvalReceivedAt } : {}),
        });

        setConfirmationRetry(null);
        const settled = settledMessage(response.messageId ?? '', response, {
          type: 'action_confirmed',
          actionId,
        });
        // Settlement is final (#2821): retire the card and remember the id.
        markSettled(settled.message.actionId ?? actionId);
        if (response.messageId) upsertAssistantMessage(settled.message);

        // Invalidate relevant caches after every confirm reply — including an
        // `unknown` outcome, where stale lists are exactly what would undermine
        // "go and check before you repeat this", and a replayed cancel, where
        // an extra refresh is harmless.
        invalidateAfterPossibleWrite();
      } catch (error) {
        const kept = keptPlanRefusal(error, actionId);
        // Never re-arm a card for an action that settled meanwhile (socket):
        // the one guard refuses it.
        if (kept && card?.actionId === actionId && armPendingConfirmation(card)) {
          const reconciling = kept.reason === 'retryable';
          // The server's pinned time wins over what this card sent.
          const receivedAt = kept.pinnedReceivedAt ?? fields?.approvalReceivedAt;
          setConfirmationRetry({
            actionId,
            reason: kept.reason,
            ...(receivedAt ? { approvalReceivedAt: receivedAt } : {}),
            ...(reconciling && receivedAt ? { locked: true } : {}),
          });
          // An earlier attempt may still be in flight behind a retryable reply.
          if (reconciling) invalidateAfterPossibleWrite();
          return;
        }
        const failure = classifyFailure(error);
        reportFailure('confirmFailed', failure, action);
        if (mayHaveWritten('confirmFailed', failure)) invalidateAfterPossibleWrite();
      } finally {
        setLoading(false);
      }
    },
    [
      conversationId,
      upsertAssistantMessage,
      setLoading,
      setPendingConfirmation,
      setConfirmationRetry,
      markSettled,
      armPendingConfirmation,
      invalidateAfterPossibleWrite,
      reportFailure,
      resolveLanguage,
    ]
  );

  const cancelAction = useCallback(
    async (actionId: string) => {
      if (!conversationId) return;
      const action = describeAction(actionId);
      setPendingConfirmation(null);
      setConfirmationRetry(null);

      try {
        const response = await api.post<ChatResponse>('/assistant/cancel', {
          conversationId,
          actionId,
          language: resolveLanguage(),
          quoteReviewVersion: 1,
          workOrderReviewVersion: 1,
          quoteReceiptConfirmation: 1,
          quoteItemChangeReview: 1,
          quoteServiceLineReview: 1,
          quoteServiceAddReview: 1,
        });

        const settled = settledMessage(response.messageId ?? '', response, {
          type: 'action_cancelled',
          actionId,
        });
        markSettled(settled.message.actionId ?? actionId);
        if (response.messageId) upsertAssistantMessage(settled.message);
        // Two ways a cancel reply can sit on a write, both read off the reply:
        // it REPLAYS a confirmation another tab already spent (#2587 review),
        // or it came after a confirmed attempt that may still commit (#2597).
        if (settled.mayHaveWritten) invalidateAfterPossibleWrite();
      } catch (error) {
        // Never swallowed. The card is already gone from this session, so a
        // silent failure reads as a successful cancel while the action is
        // still live on the server.
        const failure = classifyFailure(error);
        reportFailure('cancelFailed', failure, action);
        // Cancel itself creates nothing — but losing the GETDEL race to a
        // concurrent confirm surfaces here as a 410, and that confirm did write.
        if (mayHaveWritten('cancelFailed', failure)) invalidateAfterPossibleWrite();
      }
    },
    [
      conversationId,
      upsertAssistantMessage,
      setPendingConfirmation,
      setConfirmationRetry,
      markSettled,
      invalidateAfterPossibleWrite,
      reportFailure,
      resolveLanguage,
    ]
  );

  const loadHistory = useCallback(
    async (
      convId: string,
      options: { restorePending?: boolean; isCurrent?: () => boolean } = {}
    ): Promise<boolean> => {
      const principal = assistantPrincipalSnapshot();
      const samePrincipal = () =>
        options.isCurrent?.() !== false && sameAssistantPrincipal(principal);
      const generation = beginHistoryLoad();
      try {
        const response = await api.get<MessagesResponse>(
          `/assistant/conversations/${convId}/messages`
        );
        // A newer load, a conversation switch, or a new chat discards this GET.
        if (!samePrincipal() || useAssistantStore.getState().historyLoadGeneration !== generation) return false;

        const mapped: AssistantMessage[] = response.messages.map((m) => {
          const rowType = m.metadata?.['type'];
          const settled = rowType === 'action_confirmed' || rowType === 'action_cancelled';
          // Old rows predate explicit outcomes; absence is not proof of success.
          // A cancel row's outcome is read as for a live one (#2597).
          const outcome =
            rowType === 'action_cancelled'
              ? cancelledOutcomeOf(m.metadata?.['outcome'])
              : rowType === 'action_confirmed'
                ? (confirmedOutcomeOf(m.metadata?.['outcome']) ?? 'unknown')
                : undefined;
          // A pending card's presenter re-arms the card (malformed → its
          // description); a settled row's renders the line (malformed →
          // the generic line, never the English summary).
          const presenter = settled
            ? settledPresenterOf(m.metadata?.['presenter'])
            : presenterOf(m.metadata?.['presenter']);
          // #2587: a refusal's or failed attempt's reason, so a reload renders
          // the same localised line the live turn did.
          const failure = failureOf(m.metadata?.['failure']);
          // #2821: the receipt time a settled quote acceptance sent.
          const pinnedReceivedAt =
            rowType === 'action_confirmed'
              ? pinnedReceivedAtOf(m.metadata?.['pinnedReceivedAt'])
              : undefined;
          const quoteResult =
            rowType === 'action_confirmed'
              ? boundQuoteResult(m.metadata?.['quoteResult'], {
                  outcome,
                  conversationId: convId,
                  actionId: m.metadata?.['actionId'],
                })
              : undefined;
          const alreadyCompleted =
            rowType === 'action_confirmed' && m.metadata?.['alreadyCompleted'] === true;
          return {
            id: m.id,
            conversationId: convId,
            role: m.direction === 'inbound' ? ('user' as const) : ('assistant' as const),
            content: m.content ?? '',
            createdAt: m.createdAt,
            type: (m.metadata?.['type'] as AssistantMessage['type']) ?? 'message',
            actionId: m.metadata?.['actionId'] as string | undefined,
            toolName: m.metadata?.['toolName'] as string | undefined,
            params: m.metadata?.['params'] as Record<string, unknown> | undefined,
            // Persisted by the harness path (#2186, #2180). A reload must keep
            // the uncertainty notice and the localised card; without these two
            // a spent `unknown` plan came back as an ordinary success.
            ...(outcome ? { outcome } : {}),
            ...(presenter ? { presenter } : {}),
            ...(failure ? { failure } : {}),
            ...(pinnedReceivedAt ? { pinnedReceivedAt } : {}),
            ...(alreadyCompleted ? { alreadyCompleted: true } : {}),
            ...(quoteResult ? { quoteResult } : {}),
          };
        });

        if (!samePrincipal() || useAssistantStore.getState().historyLoadGeneration !== generation) return false;

        const sameConversation = useAssistantStore.getState().conversationId === convId;
        setConversationId(convId);
        if (!samePrincipal() || useAssistantStore.getState().historyLoadGeneration !== generation) return false;

        // Settlement is per conversation (#2960): switching restores that
        // conversation's set. A stale GET never clears another conversation's
        // ids, and never un-settles one this conversation already knows.
        if (!sameConversation) {
          setConfirmationRetry(null);
          setPendingConfirmation(null);
        }
        mergeSettled(
          mapped
            .filter(
              (m) => (m.type === 'action_confirmed' || m.type === 'action_cancelled') && m.actionId
            )
            .map((m) => m.actionId as string)
        );
        applyHistoryMessages(mapped);

        // Restore the pending-confirmation card if the last assistant turn was
        // asking for one. Prefer the merged transcript so a live pending row
        // kept from a newer turn still arms (#2960). The server still enforces
        // single-use semantics, so a stale confirmation surfaces as 410/403
        // on the legacy path — or, once the harness is on, as a 409 for a
        // rejected plan — when the user clicks it. Each is reported by
        // `confirmAction`'s failure copy.
        const lastPending = [...useAssistantStore.getState().messages]
          .reverse()
          .find((m) => m.type === 'pending_confirmation');
        if (options.restorePending !== false && lastPending && lastPending.actionId && lastPending.toolName) {
          // Through the one guard: settled ids (transcript ∪ already known)
          // are never re-armed.
          const armed = armPendingConfirmation({
            actionId: lastPending.actionId,
            toolName: lastPending.toolName,
            description: lastPending.content,
            params: lastPending.params ?? {},
            ...(lastPending.presenter ? { presenter: lastPending.presenter } : {}),
          });
          if (!armed && !sameConversation) setPendingConfirmation(null);
        } else if (!sameConversation) {
          setPendingConfirmation(null);
          setConfirmationRetry(null);
        }
        return true;
      } catch {
        return false;
      }
    },
    [
      beginHistoryLoad,
      setConversationId,
      applyHistoryMessages,
      setPendingConfirmation,
      setConfirmationRetry,
      armPendingConfirmation,
      mergeSettled,
    ]
  );

  const listConversations = useCallback(async (): Promise<ConversationSummary[]> => {
    try {
      const response = await api.get<ConversationsResponse>('/assistant/conversations');
      return response.conversations ?? [];
    } catch {
      return [];
    }
  }, []);

  /** Start a fresh conversation: clear local state, drop the active id. */
  const startNewConversation = useCallback(() => {
    clearConversation();
  }, [clearConversation]);

  return {
    sendMessage,
    confirmAction,
    cancelAction,
    loadHistory,
    listConversations,
    startNewConversation,
  };
}
